<template>
    <div class="summary" tabindex="0" role="button" aria-label="Summary">
        <p>Summary</p>
    </div>
    <div class="type">
        <span tabindex="0" role="button" :aria-label="`Summary: License (${LicenseTypeName[selectedPlanType]})`">License ({{ LicenseTypeName[selectedPlanType] }})</span>
        <p v-if="isLoading" class="loading-price">
            <LoadingDots />
        </p>
        <p v-else tabindex="0" role="button" :aria-label="`Total Cost Before Discounts: ${formatCurrency(totalCostBeforeDiscounts)}`">
            {{ formatCurrency(totalCostBeforeDiscounts) }}
        </p>
    </div>
    <div class="type" v-if="purchaseType !== PurchaseType.UPGRADE">
        <span tabindex="0" role="button" aria-label="Annual Discount">Annual Discount</span>
        <p v-if="isLoading" class="loading-price">
            <LoadingDots />
        </p>
        <p v-else tabindex="0" role="button" :aria-label="`Annual Discount Amount: ${formatCurrency(totalAnnualDiscount)}`">-{{ formatCurrency(totalAnnualDiscount) }}</p>
    </div>
    <div class="type" v-if="purchaseType !== PurchaseType.UPGRADE && totalMultiLicenseDiscount > 0">
        <span tabindex="0" role="button" aria-label="Multi License Discount">Multi License Discount</span>
        <p v-if="isLoading" class="loading-price">
            <LoadingDots />
        </p>
        <p v-else tabindex="0" role="button" :aria-label="`Multi License Discount Amount: ${formatCurrency(totalMultiLicenseDiscount)}`">-{{ formatCurrency(totalMultiLicenseDiscount) }}</p>
    </div>
    <div v-if="promoCodeApplied && promoCodeData" class="type promo-discount">
        <span tabindex="0" role="button" :aria-label="`Promo Code (${promoCodeData.code})`">Promo Code ({{ promoCodeData.code }})</span>
        <p tabindex="0" role="button" :aria-label="`Promo Code Discount Amount: ${formatCurrency(promoDiscountAmount)}`">-{{ formatCurrency(promoDiscountAmount) }}</p>
    </div>
    <div v-if="giftCertAppliedAmount > 0" class="type">
        <span tabindex="0" role="button" :aria-label="`Gift Card (${giftCertAppliedCode})`">Gift Card ({{ giftCertAppliedCode }})</span>
        <p tabindex="0" role="button" :aria-label="`Gift Card Amount: ${formatCurrency(giftCertAppliedAmount * 100)}`">-{{ formatCurrency(giftCertAppliedAmount * 100) }}</p>
    </div>
    <div v-if="store.creditBalance > 0" class="type">
        <span tabindex="0" role="button" :aria-label="`Credit Balance: ${formatCurrency(store.creditBalance * 100)}`">Credit Balance ({{ formatCurrency(store.creditBalance * 100) }})</span>
        <p tabindex="0" role="button" :aria-label="`Credit Amount Used: ${formatCurrency(store.creditAmountUsed)}`">-{{ formatCurrency(store.creditAmountUsed) }}</p>
    </div>
    <div v-if="purchaseType === PurchaseType.UPGRADE && !isLoading && upgradeDiscountItems.length > 0">
        <div v-for="(discount, index) in upgradeDiscountItems" :key="index" class="type">
            <span
                tabindex="0" role="button" :aria-label="`Upgrade Discount: ${discount.name}`"
                >{{ discount.name }}
                <span v-if="!isCredit && index === upgradeDiscountItems.length - 1" class="annotation">(1)</span></span
            >
            <p tabindex="0" role="button" :aria-label="`Upgrade Discount Amount: ${formatCurrency(Math.abs(discount.amount))}`">-{{ formatCurrency(Math.abs(discount.amount)) }}</p>
        </div>
    </div>
    <div class="due">
        <span tabindex="0" role="button" :aria-label="dueLabel">{{ dueLabel }}</span>
        <p v-if="isLoading" class="loading-price">
            <LoadingDots />
        </p>
        <p v-else tabindex="0" role="button" :aria-label="`Total Amount ${isCredit ? 'Credit Received' : 'Due Today'}: ${formatCurrency(isCredit ? Math.abs(totalCostAfterDiscounts) : totalCostAfterDiscounts)}`">
            {{ formatCurrency(isCredit ? Math.abs(totalCostAfterDiscounts) : totalCostAfterDiscounts) }}
        </p>
    </div>
    <div
        v-if="purchaseType === PurchaseType.UPGRADE && !isCredit"
        class="text-zinc-400 font-inter text-xs font-normal leading-4 mt-4"
    >
        <div v-if="isLicenseExpired">
            (1) Your current license renewal date has passed. You'll be charged the full {{ selectedTermText }} price for the new
            license. Your next billing date will be {{ formatNewRenewalDate }}.
        </div>
        <div v-else>
            (1) You're getting credit for {{ daysUntilRenewal }} days remaining on your current license. You'll be charged the
            amount above and will renew on {{ formatNewRenewalDate }}.
        </div>
    </div>
</template>
<script setup lang="ts">
import { useSubscribeStore } from '@/stores/components/modals/subscription-modal';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import LoadingDots from '~/components/main/shared/LoadingDots.vue';
import useUtils from '~/composables/useUtils';
import { LicenseRenewalTerm, LicenseTypeName, PurchaseType } from '~/types/licenses/licenses';

const store = useSubscribeStore();
const { formatCurrency } = useUtils();

const {
    selectedPlanType,
    selectedTerm,
    totalCostBeforeDiscounts,
    totalAnnualDiscount,
    totalMultiLicenseDiscount,
    totalCostAfterDiscounts,
    giftCertAppliedAmount,
    giftCertAppliedCode,
    promoCodeApplied,
    promoCodeData,
    promoDiscountAmount,
    license,
    purchaseType,
    creditAmountUsed,
    daysUntilRenewal,
    proratedUpgradeDiscount,
    isLoadingLicenseCosts,
    isLoadingUpgradePricing,
    upgradePricing,
} = storeToRefs(store);

// Use the appropriate loading state based on purchase type
const isLoading = computed(() => {
    if (purchaseType.value === PurchaseType.UPGRADE) {
        return isLoadingUpgradePricing.value;
    }
    return isLoadingLicenseCosts.value;
});

// Check if user is receiving a credit (negative amount)
const isCredit = computed(() => totalCostAfterDiscounts.value < 0);

// Due label changes based on whether it's a credit or payment
const dueLabel = computed(() => {
    return isCredit.value ? 'Credit Received' : 'Due Today';
});

// Format the renewal date for display
const formatRenewalDate = computed(() => {
    if (!license.value?.next_bill_date) {
        return '';
    }
    const date = new Date(license.value.next_bill_date);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
});

// Format the selected term for display
const selectedTermText = computed(() => {
    switch (selectedTerm.value) {
        case LicenseRenewalTerm.MONTHLY:
            return '1-month';
        case LicenseRenewalTerm.YEARLY:
            return '1-year';
        case LicenseRenewalTerm.TWO_YEARS:
            return '2-year';
        case LicenseRenewalTerm.THREE_YEARS:
            return '3-year';
        default:
            return '';
    }
});

// Check if the current license is expired (renewal date in the past)
const isLicenseExpired = computed(() => {
    return (
        purchaseType.value === PurchaseType.UPGRADE &&
        upgradePricing.value?.calculation?.days_remaining !== undefined &&
        upgradePricing.value.calculation.days_remaining <= 0
    );
});

// Check if this is an upgrade to a longer term (monthly→yearly, yearly→2yr, 2yr→3yr, etc.)
const isUpgradeToLongerTerm = computed(() => {
    return purchaseType.value === PurchaseType.UPGRADE && upgradePricing.value?.calculation?.is_upgrade_to_longer_term === true;
});

// Get upgrade discount items from API pricing
const upgradeDiscountItems = computed(() => {
    if (purchaseType.value !== PurchaseType.UPGRADE || !upgradePricing.value) {
        return [];
    }
    // Filter out zero discounts and return items that should be shown
    return upgradePricing.value.pricing.discounts.filter((d) => d.amount < 0);
});

// Calculate new renewal date for upgrades (all upgrades now reset the billing date)
const formatNewRenewalDate = computed(() => {
    // Show new renewal date for all upgrades (all upgrades reset the billing date)
    if (purchaseType.value !== PurchaseType.UPGRADE || !selectedTerm.value) {
        return '';
    }
    const today = new Date();
    const currentDay = today.getDate();
    const newRenewalDate = new Date(today);

    // Add months based on selected term
    if (selectedTerm.value === LicenseRenewalTerm.YEARLY) {
        newRenewalDate.setMonth(newRenewalDate.getMonth() + 12);
    } else if (selectedTerm.value === LicenseRenewalTerm.TWO_YEARS) {
        newRenewalDate.setMonth(newRenewalDate.getMonth() + 24);
    } else if (selectedTerm.value === LicenseRenewalTerm.THREE_YEARS) {
        newRenewalDate.setMonth(newRenewalDate.getMonth() + 36);
    } else if (selectedTerm.value === LicenseRenewalTerm.MONTHLY) {
        newRenewalDate.setMonth(newRenewalDate.getMonth() + 1);
    }

    // Handle edge cases where the day doesn't exist in the target month
    // (e.g., Jan 31 -> Feb 31 doesn't exist, so use last day of Feb)
    const lastDayOfMonth = new Date(newRenewalDate.getFullYear(), newRenewalDate.getMonth() + 1, 0).getDate();
    if (currentDay > lastDayOfMonth) {
        newRenewalDate.setDate(lastDayOfMonth);
    } else {
        newRenewalDate.setDate(currentDay);
    }

    return newRenewalDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
});
</script>
<style lang="scss" scoped>
.summary {
    @apply flex items-center gap-1 self-stretch pb-3;

    p {
        @apply text-xl font-semibold leading-8 text-zinc-900 font-inter;
    }
}

.type {
    @apply flex justify-between items-center self-stretch py-3 border-b border-dashed border-zinc-300;

    span {
        @apply text-zinc-800 font-inter text-base font-normal leading-8;
    }

    p {
        @apply text-zinc-950 text-right font-inter text-base font-medium leading-8;
    }
}

.due {
    @apply flex justify-between items-center self-stretch py-3 border-dashed border-b border-zinc-300;

    span {
        @apply text-zinc-800 font-inter text-base font-semibold leading-8;
    }

    p {
        @apply text-green-700 text-right font-inter text-base font-medium leading-8;
    }
}
.divider-div {
    @apply relative w-full flex items-center pt-8 pb-5;
    .divider {
        @apply border border-zinc-300 absolute inset-x-0;
    }
}

.due .annotation,
.type .annotation {
    @apply text-zinc-400 text-xs;
}

.promo-discount {
    span {
        @apply text-green-700 font-medium;
    }
    p {
        @apply text-green-700 font-semibold;
    }
}
</style>
